import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatDateTime,
  type CustomerInvoiceRules,
  type CustomerInvoiceRulesState,
  type UpdateCustomerInvoiceRulesRequest,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
} from '@/components/ui';

/**
 * Kortet 'Fakturaregler' på /kunder/:id: mindste antal timer, en fast retainer,
 * tidsregistrering som Excel og en fast projektkode. Reglerne gælder både /fakturering/ny og den automatiske
 * fakturering - se invoice-rules.ts i @codepass/shared. Serveren validerer; felterne her er
 * kun for at sige det tidligt.
 */

interface Form {
  minHours: string;
  retainerOn: boolean;
  retainerText: string;
  retainerQuantity: string;
  retainerPrice: string;
  retainerItemNo: string;
  timesheet: boolean;
  projectCode: string;
}

const DEFAULT_RETAINER_TEXT = 'Retainer - programming';

function formOf(rules: CustomerInvoiceRules): Form {
  return {
    minHours: rules.min_hours === null ? '' : String(rules.min_hours).replace('.', ','),
    retainerOn: rules.retainer !== null,
    retainerText: rules.retainer?.description ?? DEFAULT_RETAINER_TEXT,
    retainerQuantity: rules.retainer ? String(rules.retainer.quantity).replace('.', ',') : '1',
    retainerPrice: rules.retainer
      ? String(rules.retainer.unit_price_excl_vat).replace('.', ',')
      : '',
    retainerItemNo: rules.retainer?.bc_item_no ?? '',
    timesheet: rules.timesheet_xlsx,
    projectCode: rules.project_code ?? '',
  };
}

/** A Danish decimal: comma for decimals. `thousands` also drops dots - for amounts, not hours. */
function numberOf(raw: string, thousands = false): number | null {
  const text = (thousands ? raw.replaceAll('.', '') : raw).trim().replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** The form as the API takes it, or a Danish message about what is wrong. */
function rulesOf(form: Form): CustomerInvoiceRules | string {
  const minHours = numberOf(form.minHours);
  if (minHours !== null && !(minHours > 0))
    return 'Mindste antal timer skal være over 0 - eller tomt.';

  let retainer: CustomerInvoiceRules['retainer'] = null;
  if (form.retainerOn) {
    const quantity = numberOf(form.retainerQuantity);
    const price = numberOf(form.retainerPrice, true);
    if (!form.retainerText.trim()) return 'Retaineren skal have en tekst.';
    if (quantity === null || !(quantity > 0)) return 'Retainerens antal skal være over 0.';
    if (price === null || !(price > 0)) return 'Retainerens pris skal være over 0.';
    retainer = {
      description: form.retainerText.trim(),
      quantity,
      unit_price_excl_vat: price,
      bc_item_no: form.retainerItemNo.trim() || null,
    };
  }

  const projectCode = form.projectCode.trim().toUpperCase();
  if (projectCode.length > 20) return 'Projektkoden kan højst være 20 tegn.';

  return {
    min_hours: minHours,
    retainer,
    timesheet_xlsx: form.timesheet,
    project_code: projectCode || null,
  };
}

export function InvoiceRulesCard({ customerKey }: { customerKey: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['customer', customerKey, 'invoice-rules'];
  const canEdit = can('invoicing.settings');

  const { data: state, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      api.get<CustomerInvoiceRulesState>(
        `/customers/${encodeURIComponent(customerKey)}/invoice-rules`,
      ),
    enabled: Boolean(customerKey),
  });

  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /* The form starts from what is stored, and again when another customer is opened. */
  useEffect(() => {
    if (state) setForm(formOf(state.rules));
  }, [state]);

  /* Optimistisk: kortet står med de nye regler med det samme og rulles tilbage ved fejl. */
  const save = useMutation({
    mutationFn: (body: UpdateCustomerInvoiceRulesRequest) =>
      api.put<CustomerInvoiceRulesState>(
        `/customers/${encodeURIComponent(customerKey)}/invoice-rules`,
        body,
      ),
    onMutate: async (body) => {
      setError(null);
      setSaved(false);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CustomerInvoiceRulesState>(queryKey);
      if (previous) queryClient.setQueryData(queryKey, { ...previous, rules: body });
      return { previous };
    },
    onError: (cause, _body, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke gemme fakturareglerne.');
    },
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, result);
      setSaved(true);
    },
  });

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setSaved(false);
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const submit = () => {
    if (!form) return;
    const rules = rulesOf(form);
    if (typeof rules === 'string') {
      setError(rules);
      return;
    }
    save.mutate(rules);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fakturaregler</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4 text-sm">
        {isLoading || !state || !form ? (
          <Skeleton className="h-24" />
        ) : (
          <>
            <div>
              <Label htmlFor="rules-min-hours">Mindste antal timer pr. faktura</Label>
              <Input
                id="rules-min-hours"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Ingen"
                value={form.minHours}
                disabled={!canEdit}
                onChange={(event) => set('minHours', event.target.value)}
              />
              <p className="mt-1 text-xs text-ink-muted">
                Er der færre timer, venter den automatiske fakturering, til der er nok — timerne
                bliver liggende og kommer med på en senere faktura. /fakturering/ny advarer kun.
              </p>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.retainerOn}
                  disabled={!canEdit}
                  onChange={(event) => set('retainerOn', event.target.checked)}
                />
                <span className="font-medium">Fast retainer i stedet for timer</span>
              </label>
              {form.retainerOn ? (
                <div className="space-y-2 border-l-2 border-line pl-3">
                  <div>
                    <Label htmlFor="rules-retainer-text">Tekst på fakturaen</Label>
                    <Input
                      id="rules-retainer-text"
                      value={form.retainerText}
                      maxLength={100}
                      disabled={!canEdit}
                      onChange={(event) => set('retainerText', event.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="rules-retainer-quantity">Antal</Label>
                      <Input
                        id="rules-retainer-quantity"
                        inputMode="decimal"
                        value={form.retainerQuantity}
                        disabled={!canEdit}
                        onChange={(event) => set('retainerQuantity', event.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="rules-retainer-price">Pris ekskl. moms (DKK)</Label>
                      <Input
                        id="rules-retainer-price"
                        inputMode="decimal"
                        placeholder="60.000"
                        value={form.retainerPrice}
                        disabled={!canEdit}
                        onChange={(event) => set('retainerPrice', event.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="rules-retainer-item">Varenummer i Business Central</Label>
                    <Input
                      id="rules-retainer-item"
                      placeholder="Udviklingstimer"
                      value={form.retainerItemNo}
                      maxLength={20}
                      disabled={!canEdit}
                      onChange={(event) => set('retainerItemNo', event.target.value)}
                    />
                  </div>
                  <p className="text-xs text-ink-muted">
                    Hver faktura får én linje med retaineren — også uden timer. Timerne markeres som
                    faktureret, men koster ikke ekstra, og bilaget viser kun retaineren. Uden
                    varenummer bogføres linjen på Udviklingstimer.
                  </p>
                </div>
              ) : null}
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.timesheet}
                  disabled={!canEdit}
                  onChange={(event) => set('timesheet', event.target.checked)}
                />
                <span className="font-medium">Vedhæft tidsregistrering som Excel</span>
              </label>
              <p className="mt-1 text-xs text-ink-muted">
                Projekt, opgave, timer og dato for hver opgave på fakturaen. Vedhæftes i Business
                Central ved siden af bilaget.
              </p>
            </div>

            <div>
              <Label htmlFor="rules-project-code">Fast projektkode i Business Central</Label>
              <Input
                id="rules-project-code"
                autoComplete="off"
                placeholder="Pr. linje"
                value={form.projectCode}
                maxLength={20}
                disabled={!canEdit}
                onChange={(event) => set('projectCode', event.target.value)}
              />
              <p className="mt-1 text-xs text-ink-muted">
                Tom: hver linje får sin egen — SERVICEAFTALER på en serviceaftale, ellers
                kontraktnummeret. Udfyldt: alle kundens linjer får denne, undtagen
                applikationsdrift. Koden skal findes under dimensionen PROJECT i BC.
              </p>
            </div>

            {error ? <Alert tone="error">{error}</Alert> : null}

            {canEdit ? (
              <div className="flex items-center gap-3">
                <Button onClick={submit} loading={save.isPending} disabled={save.isPending}>
                  Gem regler
                </Button>
                {saved ? <span className="text-xs text-ink-muted">Gemt.</span> : null}
              </div>
            ) : null}

            {state.set_by_name && state.set_at ? (
              <p className="text-xs text-ink-muted">
                Sat af {state.set_by_name}, {formatDateTime(state.set_at)}.
              </p>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
